import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  GraduationCap,
  Heart,
  Sparkles,
  Leaf,
  Users,
  BookOpen,
  Brain,
  ArrowRight,
  Star,
  Send,
  Loader2,
  BarChart3,
  Calendar,
  TrendingUp,
  MessageCircle,
  FileText,
  Zap,
  LogIn,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = window.location.origin;

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, isAdmin, loading } = useAuth();
  
  // Stats per sezione PRO
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  
  // Solea Chat
  const [chatQuery, setChatQuery] = useState("");
  const [chatResponse, setChatResponse] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  // Carica stats se utente admin
  useEffect(() => {
    if (user && isAdmin && !loading) {
      fetchStats();
    }
  }, [user, isAdmin, loading]);

  const fetchStats = async () => {
    setLoadingStats(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admin/mammouth/stats-context`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.log("Stats non disponibili per questo utente");
    } finally {
      setLoadingStats(false);
    }
  };

  // Solea Chat - versione pubblica semplificata
  const handleSoleaChat = async () => {
    if (!chatQuery.trim()) {
      toast.error("Scrivi una domanda per Solea AI");
      return;
    }

    setChatLoading(true);
    setChatResponse(null);

    try {
      // Se loggato, usa Mammouth completo
      if (user) {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_URL}/api/admin/mammouth/analyze`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            query: chatQuery,
            context: "Domanda dal pubblico sulla piattaforma ODR Academy e benessere oncologico.",
          }),
        });

        if (response.ok) {
          const data = await response.json();
          setChatResponse(data.response);
        } else {
          setChatResponse("Mi dispiace, non riesco a rispondere in questo momento. Prova più tardi!");
        }
      } else {
        // Versione pubblica - risposta generica
        setChatResponse(`Grazie per la tua domanda su "${chatQuery}"! 
        
Per ricevere risposte personalizzate da Solea AI, registrati gratuitamente o accedi alla sezione Benessere.

🌿 Intanto, esplora i nostri articoli gratuiti sul benessere oncologico!`);
      }
      setChatQuery("");
    } catch (error) {
      setChatResponse("Errore di connessione. Riprova più tardi.");
    } finally {
      setChatLoading(false);
    }
  };

  // Se sta caricando l'auth
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-800 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-300 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-purple-200">Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      
      {/* ============== HERO SECTION ============== */}
      <div className="relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700"></div>
        
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500/30 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-500/30 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 px-4 py-16 md:py-24">
          <div className="max-w-5xl mx-auto text-center">
            {/* Logo */}
            <div className="mb-6">
              <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-2">
                <GraduationCap className="w-8 h-8 text-amber-300" />
                <span className="text-white font-semibold text-lg">ODR Academy</span>
              </div>
            </div>

            {/* Main Title */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Benessere per
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-rose-300">
                Estetiste Oncologiche
              </span>
            </h1>

            <p className="text-lg md:text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
              Articoli Gratuiti • Corsi Professionali • AI Solea • Trend Cosmoprof 2026
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap justify-center gap-4">
              {!user ? (
                <>
                  <Button 
                    onClick={() => navigate("/login")}
                    className="bg-white text-indigo-700 hover:bg-purple-50 px-8 py-6 text-lg rounded-full shadow-lg"
                  >
                    <LogIn className="w-5 h-5 mr-2" />
                    Accedi PRO
                  </Button>
                  <Button 
                    onClick={() => navigate("/benessere")}
                    variant="outline"
                    className="border-2 border-white/50 text-white hover:bg-white/10 px-8 py-6 text-lg rounded-full"
                  >
                    Esplora Gratis
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    onClick={() => navigate("/admin")}
                    className="bg-white text-indigo-700 hover:bg-purple-50 px-8 py-6 text-lg rounded-full shadow-lg"
                  >
                    <BarChart3 className="w-5 h-5 mr-2" />
                    Dashboard
                  </Button>
                  <Button 
                    onClick={() => navigate("/sessioni")}
                    variant="outline"
                    className="border-2 border-white/50 text-white hover:bg-white/10 px-8 py-6 text-lg rounded-full"
                  >
                    Le Mie Sessioni
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ============== SEZIONE A: PRO (Solo se loggato) ============== */}
      {user && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-b-4 border-amber-200">
          <div className="max-w-6xl mx-auto px-4 py-8">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center">
                <Star className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-xl font-bold text-amber-800">Area PRO</h2>
              <span className="text-sm text-amber-600 ml-2">Benvenuto, {user.name || user.email}</span>
            </div>

            <div className="grid md:grid-cols-4 gap-4">
              {/* Stats Cards - Solo per Admin */}
              {isAdmin ? (
                loadingStats ? (
                  <div className="md:col-span-3 flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
                  </div>
                ) : stats ? (
                  <>
                    <Card className="bg-white/80 border-amber-200">
                      <CardContent className="p-4 text-center">
                        <Calendar className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
                        <p className="text-3xl font-bold text-indigo-700">{stats.sessioni_totali || 0}</p>
                        <p className="text-sm text-slate-600">Sessioni</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-white/80 border-amber-200">
                      <CardContent className="p-4 text-center">
                        <FileText className="w-8 h-8 text-green-600 mx-auto mb-2" />
                        <p className="text-3xl font-bold text-green-700">{stats.iscrizioni_totali || 0}</p>
                        <p className="text-sm text-slate-600">Iscrizioni</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-white/80 border-amber-200">
                      <CardContent className="p-4 text-center">
                        <Users className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                        <p className="text-3xl font-bold text-purple-700">{stats.utenti_totali || 0}</p>
                        <p className="text-sm text-slate-600">Utenti</p>
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  <div className="md:col-span-3 flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-amber-600 mr-2" />
                    <span className="text-amber-700">Caricamento statistiche...</span>
                  </div>
                )
              ) : (
                /* Link rapidi per utenti non-admin */
                <>
                  <Card className="bg-white/80 border-amber-200 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate("/sessioni")}>
                    <CardContent className="p-4 text-center">
                      <Calendar className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
                      <p className="font-semibold text-slate-700">Le Mie Sessioni</p>
                      <p className="text-xs text-slate-500">Visualizza e gestisci</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-white/80 border-amber-200 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate("/materiali")}>
                    <CardContent className="p-4 text-center">
                      <FileText className="w-8 h-8 text-green-600 mx-auto mb-2" />
                      <p className="font-semibold text-slate-700">Materiali</p>
                      <p className="text-xs text-slate-500">Documenti e risorse</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-white/80 border-amber-200 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate("/calendario")}>
                    <CardContent className="p-4 text-center">
                      <Users className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                      <p className="font-semibold text-slate-700">Calendario</p>
                      <p className="text-xs text-slate-500">Prossimi eventi</p>
                    </CardContent>
                  </Card>
                </>
              )}

              {/* Mammouth Button - Solo per Admin */}
              {isAdmin && (
                <Card className="bg-gradient-to-br from-purple-600 to-indigo-700 border-0">
                  <CardContent className="p-4 text-center">
                    <Brain className="w-8 h-8 text-white mx-auto mb-2" />
                    <p className="text-white font-semibold mb-2">Mammouth AI</p>
                    <Button 
                      onClick={() => navigate("/admin/mammouth-ai")}
                      variant="secondary"
                      size="sm"
                      className="w-full"
                    >
                      Analizza Dati
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============== SEZIONE B: PUBBLICA (Sempre visibile) ============== */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-slate-800 mb-2">
            🌿 Risorse Gratuite per il Tuo Benessere
          </h2>
          <p className="text-slate-600">Esplora articoli, tecniche e parla con la nostra AI</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          
          {/* Card: Articoli Benessere */}
          <Card className="group hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-emerald-200 overflow-hidden">
            <div className="h-3 bg-gradient-to-r from-emerald-400 to-teal-500"></div>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center shrink-0">
                  <BookOpen className="w-7 h-7 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-slate-800 mb-2">
                    📖 Articoli Benessere
                  </h3>
                  <p className="text-slate-600 mb-4">
                    Stress, Pelle Oncologica, Protocolli 2026, Meditazione, Alimentazione e molto altro.
                  </p>
                  <div className="flex items-center gap-2 text-sm text-emerald-600 mb-4">
                    <Sparkles className="w-4 h-4" />
                    <span>12+ Articoli Pubblicati</span>
                  </div>
                  <Link to="/benessere/articoli">
                    <Button className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700">
                      Leggi Gratis
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Card: Tecniche di Benessere */}
          <Card className="group hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-purple-200 overflow-hidden">
            <div className="h-3 bg-gradient-to-r from-purple-400 to-pink-500"></div>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center shrink-0">
                  <Leaf className="w-7 h-7 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-slate-800 mb-2">
                    🧘 Tecniche di Benessere
                  </h3>
                  <p className="text-slate-600 mb-4">
                    Meditazione, Respirazione, Gestione Stress, Nervo Vago, Yoga e pratiche guidate.
                  </p>
                  <div className="flex items-center gap-2 text-sm text-purple-600 mb-4">
                    <Heart className="w-4 h-4" />
                    <span>Guide Step-by-Step</span>
                  </div>
                  <Link to="/benessere/tecniche">
                    <Button className="w-full bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700">
                      Esplora Tecniche
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Solea AI Chat */}
        <Card className="mt-8 border-2 border-indigo-100 bg-gradient-to-br from-indigo-50 to-purple-50">
          <div className="h-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500"></div>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-800">🤖 Solea AI Chat</h3>
                <p className="text-sm text-slate-600">Chiedi consigli personalizzati sul benessere</p>
              </div>
            </div>

            <div className="flex gap-3">
              <Input
                value={chatQuery}
                onChange={(e) => setChatQuery(e.target.value)}
                placeholder="Es: Come gestire lo stress? Consigli per la pelle sensibile?"
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSoleaChat();
                  }
                }}
              />
              <Button 
                onClick={handleSoleaChat}
                disabled={chatLoading}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
              >
                {chatLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </div>

            {chatResponse && (
              <div className="mt-4 p-4 bg-white rounded-xl border border-indigo-100">
                <div className="flex items-center gap-2 mb-2 text-indigo-600">
                  <Brain className="w-4 h-4" />
                  <span className="text-sm font-medium">Solea AI</span>
                </div>
                <p className="text-slate-700 whitespace-pre-wrap">{chatResponse}</p>
              </div>
            )}

            {!user && (
              <p className="text-xs text-center text-slate-500 mt-4">
                💡 <Link to="/login" className="text-indigo-600 hover:underline">Accedi</Link> per risposte personalizzate con Mammouth AI
              </p>
            )}
          </CardContent>
        </Card>

        {/* Quick Links Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          <Link to="/benessere" className="group">
            <Card className="hover:shadow-lg transition-all hover:border-emerald-300 h-full">
              <CardContent className="p-4 text-center">
                <Leaf className="w-8 h-8 text-emerald-600 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-slate-700">Home Benessere</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/registrazione-benessere" className="group">
            <Card className="hover:shadow-lg transition-all hover:border-purple-300 h-full">
              <CardContent className="p-4 text-center">
                <Users className="w-8 h-8 text-purple-600 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-slate-700">Registrati Gratis</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/login" className="group">
            <Card className="hover:shadow-lg transition-all hover:border-indigo-300 h-full">
              <CardContent className="p-4 text-center">
                <GraduationCap className="w-8 h-8 text-indigo-600 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-slate-700">Area PRO</p>
              </CardContent>
            </Card>
          </Link>
          <a href="https://odr-academy.it" target="_blank" rel="noopener noreferrer" className="group">
            <Card className="hover:shadow-lg transition-all hover:border-amber-300 h-full">
              <CardContent className="p-4 text-center">
                <TrendingUp className="w-8 h-8 text-amber-600 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-slate-700">Sito Ufficiale</p>
              </CardContent>
            </Card>
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-8 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <GraduationCap className="w-6 h-6 text-amber-400" />
            <span className="font-semibold">ODR Academy</span>
          </div>
          <p className="text-slate-400 text-sm mb-4">
            Formazione professionale in estetica oncologica e benessere
          </p>
          <div className="flex justify-center gap-6 text-sm text-slate-400">
            <Link to="/benessere" className="hover:text-white transition-colors">Benessere</Link>
            <Link to="/login" className="hover:text-white transition-colors">Accedi</Link>
            <Link to="/registrazione-benessere" className="hover:text-white transition-colors">Registrati</Link>
          </div>
          <p className="text-slate-500 text-xs mt-6">
            © {new Date().getFullYear()} ODR Academy - Estetica Oncologica. Tutti i diritti riservati.
          </p>
        </div>
      </footer>
    </div>
  );
}
